import { StandardEvents } from '@shopify/events';

/**
 * Virtual Games · ficha de producto.
 *
 * <vg-variant-facts> mantiene sincronizadas con la variante elegida las píldoras de datos que
 * renderiza snippets/vg-pdp-facts.liquid (bloques vg-key-facts y vg-platform-badges).
 *
 * El servidor entrega el estado inicial y un mapa JSON:
 *   { "product": { "plataforma": "…", … }, "variants": { "<id>": { … } } }
 * El elemento solo cambia textos y muestra u oculta ítems, así que funciona igual en la ficha,
 * en "Producto destacado" y en el quick add, sin depender del HTML que devuelva cada fetch.
 *
 * Escucha `shopify:product:select` (StandardEvents.productSelect), que dispara
 * assets/variant-picker.js, en su sección o diálogo:
 *   - event.detail.variantId: cambio inmediato con la variante del valor elegido.
 *   - event.promise → detail.resource.id: variante que resolvió Horizon
 *     (null = combinación sin variante → datos del producto).
 *
 * Idempotente: conectar y desconectar (re-render del editor, morph, quick add) solo agrega o
 * quita su único listener. No anima nada.
 */
class VgVariantFacts extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();

    const scope = this.closest('.shopify-section, dialog') ?? document;
    scope.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, {
      signal: this.#controller.signal,
    });

    // Si la variante cambió antes de que cargara este módulo, la URL ya la tiene (?variant=).
    if (!this.closest('dialog')) {
      const urlVariantId = new URLSearchParams(window.location.search).get('variant');
      if (urlVariantId) this.update(urlVariantId);
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /**
   * @param {Event & { detail?: { variantId?: string }, promise?: Promise<{ detail?: { productId?: string, newProduct?: object, resource?: { id?: number | string } | null } }> }} event
   */
  #handleProductSelect = (event) => {
    const source = event.target;
    if (!(source instanceof HTMLElement) || source.closest('product-card')) return;

    const sourceProductId = source.dataset.productId;
    const productId = this.dataset.productId;
    if (sourceProductId && productId && sourceProductId !== productId) return;

    const variantId = event.detail?.variantId;
    if (variantId) this.update(variantId);

    event.promise
      ?.then((result) => {
        const detail = result?.detail;
        if (!detail) return;

        const currentProductId = this.dataset.productId;
        if (!detail.newProduct && detail.productId && currentProductId && String(detail.productId) !== currentProductId) {
          return;
        }

        this.update(detail.resource?.id ?? null);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[vg-variant-facts]', error);
      });
  };

  /**
   * Muestra los datos de una variante, o los del producto si `variantId` es null.
   * Ignora ids que no están en el mapa (por ejemplo, de otro producto).
   * @param {string | number | null} variantId
   */
  update(variantId) {
    const map = this.#readMap();
    if (!map) return;

    const id = variantId == null ? null : String(variantId).split('/').pop();
    const facts = id ? map.variants?.[id] : map.product;
    if (!facts) return;

    let visible = 0;

    for (const item of this.querySelectorAll('[data-vg-fact]')) {
      if (!(item instanceof HTMLElement)) continue;

      const key = item.dataset.vgFact ?? '';
      const raw = facts[key];
      const value = typeof raw === 'string' ? raw.trim() : '';
      const match = item.dataset.vgFactMatch;
      const show = match === undefined ? value !== '' : value === match;

      if (match === undefined && show) {
        const output = item.querySelector('[data-vg-fact-value]');
        if (output && output.textContent !== value) output.textContent = value;
      }

      item.hidden = !show;
      if (show) visible += 1;
    }

    const list = this.querySelector('[data-vg-facts-list]');
    if (list instanceof HTMLElement) list.hidden = visible === 0;
    if (this.hasAttribute('data-hide-when-empty')) this.hidden = visible === 0;
  }

  /**
   * @returns {{ product?: Record<string, string>, variants?: Record<string, Record<string, string>> } | null}
   */
  #readMap() {
    const script = this.querySelector('script[data-vg-facts-map]');
    if (!script?.textContent) return null;

    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  }
}

if (!customElements.get('vg-variant-facts')) {
  customElements.define('vg-variant-facts', VgVariantFacts);
}
